import './pagination.scss';
import React from 'react';
import classnames from 'classnames';

type PaginationProps = {
    totalPages: number;
    currentPage: number;
    onClick: (pageNumber: number) => void;
};

const Pagination = ({ totalPages, currentPage, onClick }: PaginationProps) => {
    const goToPage = (event: React.MouseEvent<HTMLLIElement>) => {
        const pageIndex = event.currentTarget.getAttribute('data-page-index');
        if (pageIndex) {
            let page = parseInt(pageIndex, 10);
            page = Math.max(1, page);
            page = Math.min(totalPages, page);
            onClick(page);
        }
    };

    const createPages = () => {
        const pagesList: React.ReactNode[] = [];

        for (let i = 2; i <= totalPages - 1; i += 1) {
            const classes = classnames({
                page: true,
                'is-hidden': i < currentPage - 1 || i > currentPage + 1,
                'is-current': i === currentPage
            });

            pagesList.push(
                <li
                    onClick={goToPage}
                    data-page-index={i}
                    key={i + 1}
                    className={classes}
                    data-testid={`pagination-page-${i}`}
                >
                    {i}
                </li>
            );
        }

        return pagesList;
    };

    const classes = classnames({
        'b-pagination': true,
        'is-hidden': totalPages <= 1
    });

    const leftDotsClasses = classnames({
        page: true,
        'is-hidden': currentPage <= 2
    });

    const rightDotsClasses = classnames({
        page: true,
        'is-hidden': currentPage > totalPages - 2
    });

    const leftArrowClasses = classnames({
        page: true,
        arrow: true,
        'is-left': true,
        'is-disabled': currentPage === 1
    });

    const rightArrowClasses = classnames({
        page: true,
        arrow: true,
        'is-right': true,
        'is-disabled': currentPage === totalPages
    });

    const firstPageClasses = classnames({
        page: true,
        'is-current': currentPage === 1
    });

    const lastPageClasses = classnames({
        page: true,
        'is-current': totalPages === currentPage
    });

    return (
        <div className={classes} data-testid="pagination">
            <div>
                <ul className="pages-list">
                    <li
                        data-page-index={currentPage - 1}
                        onClick={goToPage}
                        key={0}
                        className={leftArrowClasses}
                        data-testid="pagination-left-arrow"
                    />
                    <li
                        data-page-index={1}
                        onClick={goToPage}
                        key={1}
                        className={firstPageClasses}
                        data-testid="pagination-first-page"
                    >
                        1
                    </li>
                    <li
                        data-page-index={currentPage - 3}
                        onClick={goToPage}
                        key={2}
                        className={leftDotsClasses}
                        data-testid="pagination-left-dots"
                    >
                        ...
                    </li>
                    {createPages()}
                    <li
                        data-page-index={currentPage + 3}
                        onClick={goToPage}
                        key={3}
                        className={rightDotsClasses}
                        data-testid="pagination-right-dots"
                    >
                        ...
                    </li>
                    <li
                        data-page-index={totalPages}
                        onClick={goToPage}
                        key={4}
                        className={lastPageClasses}
                        data-testid="pagination-last-page"
                    >
                        {totalPages}
                    </li>
                    <li
                        data-page-index={currentPage + 1}
                        onClick={goToPage}
                        key={5}
                        className={rightArrowClasses}
                        data-testid="pagination-right-arrow"
                    />
                </ul>
            </div>
        </div>
    );
};

export default Pagination;
